// MonitorPaymentGateway.js
import React, { useEffect, useState } from "react";
import { Box, Text, Badge, Grid, GridItem, Divider } from "@chakra-ui/react";
import { AlarmCheck, Cable } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ResponsiveTable from "components/Tables/InfoTable";
import InfoCard from "components/Card/InfoCard";

// Sample Data for Gateway Monitoring Metrics
const gatewayMetrics = {
  requestsPerSecond: [
    { time: "00:00", rps: 50 },
    { time: "00:30", rps: 60 },
    { time: "01:00", rps: 55 },
  ],
  errorRates: [
    { time: "00:00", errors: 0.5 },
    { time: "00:30", errors: 0.3 },
    { time: "01:00", errors: 0.4 },
  ],
  latency: [
    { time: "00:00", latency: 200 },
    { time: "00:30", latency: 150 },
    { time: "01:00", latency: 180 },
  ],
  dataTraffic: [
    { time: "00:00", uploaded: 100, downloaded: 200 },
    { time: "00:30", uploaded: 150, downloaded: 250 },
    { time: "01:00", uploaded: 130, downloaded: 220 },
  ],
};

const MonitorPaymentGateway = () => {
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGatewayStatus = async () => {
      // Replace with your actual API call to fetch gateway status
      try {
        const response = await fetch("/api/payment-gateway/status"); // Example endpoint
        const data = await response.json();
        setGatewayStatus(data);
      } catch (error) {
        console.error("Error fetching gateway status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGatewayStatus();
  }, []);

  const headers = [
    { label: "Name", accessor: () => "Gateway-Response-Time" },
    {
      label: "Status",
      render: () => (
        <Badge
          colorScheme="green"
          variant="outline"
          borderRadius="8px"
          height="20px"
          px="8px"
        >
          Operational
        </Badge>
      ),
    },
    {
      label: "Last Checked",
      accessor: (data) => data?.lastChecked || "2024-12-08 12:44PM",
    },
    { label: "Conditions", accessor: () => "Response time exceeds 5s" },
  ];

  //const gatewayStatus = { lastChecked: "2024-10-24 09:30AM" };

  return (
    <Box p={6}>
      {/* Alarms Table */}
      <InfoCard
        heading="Alarms"
        icon={<AlarmCheck />}
        headingFontSize="md"
        headingFontWeight="500"
        headingColor="gray.500"
      >
        {" "}
        <ResponsiveTable headers={headers} data={gatewayStatus} />
      </InfoCard>
      
      <Divider my={4}/>

      {/* Metrics Section */}
      <InfoCard
        heading="Metrics"
        icon={<Cable />}
        headingFontSize="md"
        headingFontWeight="500"
        headingColor="gray.500"
      >
        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
          {/* Requests Per Second */}
          <GridItem>
            <Box p={4} border="1px solid #e2e8f0" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={2}>
                Requests Per Second (RPS)
              </Text>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={gatewayMetrics.requestsPerSecond}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="rps"
                    stroke="#82ca9d"
                    name="RPS"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </GridItem>

          {/* Error Rates */}
          <GridItem>
            <Box p={4} border="1px solid #e2e8f0" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={2}>
                Error Rates (%)
              </Text>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={gatewayMetrics.errorRates}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="errors"
                    stroke="#ff6b6b"
                    name="Error Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </GridItem>

          {/* Latency */}
          <GridItem>
            <Box p={4} border="1px solid #e2e8f0" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={2}>
                Latency (ms)
              </Text>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={gatewayMetrics.latency}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="latency"
                    stroke="#8884d8"
                    name="Latency"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </GridItem>

          {/* Data Traffic */}
          <GridItem>
            <Box p={4} border="1px solid #e2e8f0" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={2}>
                Data Traffic (KiB/sec)
              </Text>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={gatewayMetrics.dataTraffic}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="uploaded"
                    stroke="#82ca9d"
                    name="Uploaded"
                  />
                  <Line
                    type="monotone"
                    dataKey="downloaded"
                    stroke="#8884d8"
                    name="Downloaded"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </GridItem>
        </Grid>
      </InfoCard>
      
    </Box>
  );
};

export default MonitorPaymentGateway;
